import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ResetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const SortFilterModal = ({
  isOpen,
  onClose,
  mode, // 'cards' | 'binders' | 'binder-detail' | 'wishlist-sets' | 'wishlist-cards' | 'cards-sets' | 'set-cards'
  isOwnCollection,
  // Filter state
  tradeFilter, // 'all' | 'pc' | 'tradable'
  setTradeFilter,
  visibilityFilter, // 'all' | 'public' | 'private'
  setVisibilityFilter,
  imageFilter, // 'digital' | 'photo'
  setImageFilter,
  // Sort state
  sortBy,
  setSortBy,
  // Sets filter (for cards-sets mode)
  setsFilter, // 'all' | 'owned'
  setSetsFilter,
  // Sets language (for cards-sets mode)
  setsLanguage, // 'en' | 'ja' | 'zh-tw' | etc.
  setSetsLanguage,
  // Reset handler
  onReset,
}) => {
  const { theme } = useTheme();
  const [languageExpanded, setLanguageExpanded] = useState(false);

  if (!isOpen) return null;

  const isBinderMode = mode === 'binders' || mode === 'binder-detail';
  const isWishListMode = mode === 'wishlist-sets' || mode === 'wishlist-cards';
  const isCardsSetsMode = mode === 'cards-sets';
  const isSetCardsMode = mode === 'set-cards'; // Cards within a selected set

  const tradeOptions = [
    { id: 'all', label: 'All' },
    { id: 'pc', label: 'PC' },
    { id: 'tradable', label: 'Tradable' },
  ];

  const visibilityOptions = [
    { id: 'all', label: 'All' },
    { id: 'public', label: 'Public' },
    { id: 'private', label: 'Private' },
  ];

  const imageOptions = [
    { id: 'digital', label: 'Digital' },
    { id: 'photo', label: 'Photo' },
  ];

  const setsFilterOptions = [
    { id: 'all', label: 'All Sets' },
    { id: 'owned', label: 'Owned Only' },
  ];

  const languageOptions = [
    { id: 'en', label: 'English' },
    { id: 'ja', label: 'Japanese' },
    { id: 'zh-tw', label: 'Chinese (Traditional)' },
    { id: 'es', label: 'Spanish' },
    { id: 'fr', label: 'French' },
    { id: 'it', label: 'Italian' },
    { id: 'de', label: 'German' },
    { id: 'pt', label: 'Portuguese' },
  ];

  const getCurrentLanguageLabel = () => {
    const lang = languageOptions.find(l => l.id === setsLanguage);
    return lang?.label || 'English';
  };

  const cardSortOptions = [
    { id: 'name-asc', label: 'Name A-Z' },
    { id: 'name-desc', label: 'Name Z-A' },
    { id: 'type', label: 'Type' },
    { id: 'recent', label: 'Recently Added' },
    { id: 'hp', label: 'HP High-Low' },
  ];

  const binderSortOptions = [
    { id: 'name-asc', label: 'A-Z' },
    { id: 'name-desc', label: 'Z-A' },
  ];

  const setsSortOptions = [
    { id: 'newest', label: 'Newest First' },
    { id: 'oldest', label: 'Oldest First' },
    { id: 'a-z', label: 'A-Z' },
    { id: 'z-a', label: 'Z-A' },
  ];

  const wishListSetsSortOptions = [
    { id: 'newest', label: 'Newest First' },
    { id: 'oldest', label: 'Oldest First' },
    { id: 'name-asc', label: 'A-Z' },
    { id: 'name-desc', label: 'Z-A' },
  ];

  const wishListCardsSortOptions = [
    { id: 'recent', label: 'Recently Added' },
    { id: 'name-asc', label: 'Name A-Z' },
    { id: 'name-desc', label: 'Name Z-A' },
  ];

  // Sort options for cards within a set
  const setCardsSortOptions = [
    { id: 'number-asc', label: 'Card # (Low-High)' },
    { id: 'number-desc', label: 'Card # (High-Low)' },
    { id: 'name-asc', label: 'Name A-Z' },
    { id: 'name-desc', label: 'Name Z-A' },
  ];

  const sortOptions = isSetCardsMode
    ? setCardsSortOptions
    : isBinderMode
    ? binderSortOptions
    : isCardsSetsMode
      ? setsSortOptions
      : mode === 'wishlist-sets'
        ? wishListSetsSortOptions
        : mode === 'wishlist-cards'
          ? wishListCardsSortOptions
          : cardSortOptions;

  const getTitle = () => {
    if (isBinderMode) return 'Sort Binders';
    if (isWishListMode) return 'Sort Wish List';
    if (isCardsSetsMode) return 'Sort & Filter Sets';
    if (isSetCardsMode) return 'Sort Cards';
    return 'Filter & Sort';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{...styles.backdrop, backgroundColor: theme.overlay}}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{...styles.modal, backgroundColor: theme.bgSheet}}
          >
            {/* Handle */}
            <div style={styles.handle} />

            {/* Header */}
            <div style={styles.header}>
              <h2 style={{...styles.title, color: theme.text}}>
                {getTitle()}
              </h2>
              <div style={styles.headerButtons}>
                {onReset && (
                  <button
                    style={{...styles.resetButton, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary}}
                    onClick={() => {
                      onReset();
                      onClose();
                    }}
                  >
                    <ResetIcon />
                    <span>Reset</span>
                  </button>
                )}
                <button style={{...styles.closeButton, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary}} onClick={onClose}>
                  <CloseIcon />
                </button>
              </div>
            </div>

            {/* Language Selector - Only for cards-sets mode */}
            {isCardsSetsMode && setSetsLanguage && (
              <>
                <div style={styles.section}>
                  <button
                    style={{
                      ...styles.languageSelector,
                      backgroundColor: theme.bgCard,
                      borderColor: languageExpanded ? theme.accent : theme.border,
                    }}
                    onClick={() => setLanguageExpanded(!languageExpanded)}
                  >
                    <span style={{ color: theme.text, fontWeight: '500' }}>
                      {getCurrentLanguageLabel()}
                    </span>
                    <ChevronIcon expanded={languageExpanded} />
                  </button>

                  <AnimatePresence>
                    {languageExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div style={styles.languageList}>
                          {languageOptions.map((option) => (
                            <button
                              key={option.id}
                              style={{
                                ...styles.languageOption,
                                backgroundColor: setsLanguage === option.id ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                                borderColor: setsLanguage === option.id ? theme.accent : theme.border,
                                color: setsLanguage === option.id ? theme.accent : theme.text,
                              }}
                              onClick={() => {
                                setSetsLanguage(option.id);
                                setLanguageExpanded(false);
                              }}
                            >
                              {option.label}
                              {setsLanguage === option.id && (
                                <span style={{ color: theme.accent }}><CheckIcon /></span>
                              )}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div style={{...styles.divider, backgroundColor: theme.border}} />
              </>
            )}

            {/* Sets Filter Section - Only for cards-sets mode */}
            {isCardsSetsMode && setSetsFilter && (
              <>
                <div style={styles.section}>
                  <label style={{...styles.label, color: theme.textSecondary}}>Show</label>
                  <div style={styles.chipRow}>
                    {setsFilterOptions.map((option) => (
                      <button
                        key={option.id}
                        style={{
                          ...styles.chip,
                          backgroundColor: setsFilter === option.id ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                          borderColor: setsFilter === option.id ? theme.accent : theme.border,
                          color: setsFilter === option.id ? theme.accent : theme.textMuted,
                        }}
                        onClick={() => setSetsFilter(option.id)}
                      >
                        {option.label}
                        {setsFilter === option.id && (
                          <span style={styles.chipCheck}><CheckIcon /></span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{...styles.divider, backgroundColor: theme.border}} />
              </>
            )}

            {/* Filter Section - Only for cards mode (not binders, wish list, cards-sets, or set-cards) */}
            {!isBinderMode && !isWishListMode && !isCardsSetsMode && !isSetCardsMode && (
              <>
                <div style={styles.section}>
                  <label style={{...styles.label, color: theme.textSecondary}}>Show</label>
                  <div style={styles.chipRow}>
                    {tradeOptions.map((option) => (
                      <button
                        key={option.id}
                        style={{
                          ...styles.chip,
                          backgroundColor: tradeFilter === option.id ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                          borderColor: tradeFilter === option.id ? theme.accent : theme.border,
                          color: tradeFilter === option.id ? theme.accent : theme.textMuted,
                        }}
                        onClick={() => setTradeFilter(option.id)}
                      >
                        {option.label}
                        {tradeFilter === option.id && (
                          <span style={styles.chipCheck}><CheckIcon /></span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Visibility filter - only for own collection */}
                {isOwnCollection && (
                  <div style={styles.section}>
                    <label style={{...styles.label, color: theme.textSecondary}}>Visibility</label>
                    <div style={styles.chipRow}>
                      {visibilityOptions.map((option) => (
                        <button
                          key={option.id}
                          style={{
                            ...styles.chip,
                            backgroundColor: visibilityFilter === option.id ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                            borderColor: visibilityFilter === option.id ? theme.accent : theme.border,
                            color: visibilityFilter === option.id ? theme.accent : theme.textMuted,
                          }}
                          onClick={() => setVisibilityFilter(option.id)}
                        >
                          {option.label}
                          {visibilityFilter === option.id && (
                            <span style={styles.chipCheck}><CheckIcon /></span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Image type filter */}
                <div style={styles.section}>
                  <label style={{...styles.label, color: theme.textSecondary}}>Image Type</label>
                  <div style={styles.chipRow}>
                    {imageOptions.map((option) => (
                      <button
                        key={option.id}
                        style={{
                          ...styles.chip,
                          backgroundColor: imageFilter === option.id ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                          borderColor: imageFilter === option.id ? theme.accent : theme.border,
                          color: imageFilter === option.id ? theme.accent : theme.textMuted,
                        }}
                        onClick={() => setImageFilter(option.id)}
                      >
                        {option.label}
                        {imageFilter === option.id && (
                          <span style={styles.chipCheck}><CheckIcon /></span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{...styles.divider, backgroundColor: theme.border}} />
              </>
            )}

            {/* Sort Section */}
            <div style={styles.section}>
              <label style={{...styles.label, color: theme.textSecondary}}>Sort By</label>
              <div style={styles.sortList}>
                {sortOptions.map((option) => (
                  <button
                    key={option.id}
                    style={{
                      ...styles.sortOption,
                      backgroundColor: sortBy === option.id ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                      borderColor: sortBy === option.id ? theme.accent : theme.border,
                      color: theme.text,
                    }}
                    onClick={() => {
                      setSortBy(option.id);
                      onClose();
                    }}
                  >
                    <span style={styles.sortLabel}>{option.label}</span>
                    {sortBy === option.id && (
                      <span style={{...styles.sortCheck, color: theme.accent}}><CheckIcon /></span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  modal: {
    position: 'fixed',
    bottom: '80px',
    left: '8px',
    right: '8px',
    borderRadius: '20px',
    padding: '12px 20px 24px',
    zIndex: 1001,
    overflow: 'hidden',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: {
    width: '36px',
    height: '4px',
    backgroundColor: '#3a3a3a',
    borderRadius: '2px',
    margin: '0 auto 16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  resetButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  section: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  chipRow: {
    display: 'flex',
    gap: '8px',
  },
  chip: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    outline: '0',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  chipCheck: {
    display: 'flex',
    alignItems: 'center',
  },
  divider: {
    height: '1px',
    margin: '16px 0',
  },
  sortList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  sortOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    border: '1px solid',
    borderRadius: '10px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    outline: '0',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  sortLabel: {
    fontWeight: '500',
  },
  sortCheck: {
    display: 'flex',
    alignItems: 'center',
  },
  toggleButton: {
    padding: '6px 14px',
    borderRadius: '16px',
    border: '1px solid',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: 'all 0.15s ease',
  },
  toggleLabel: {
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  languageSelector: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    border: '1px solid',
    borderRadius: '10px',
    fontSize: '15px',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: 'border-color 0.15s ease',
  },
  languageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '8px',
  },
  languageOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    border: '1px solid',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
  },
};

export default SortFilterModal;
